import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Modal,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdLogsService from "../../../services/OrderingServices/AdLogsService";
import AdsService from "../../../services/OrderingServices/AdsService";
import AdLogsDetailFilterCard from "../component/AdLogsDetailFilterCard";
import ExportToExcel from "../../../Components/Common/ExportToExcel";
import {
  formatDate,
  formatDateTimeWithSeconds,
  getTimeOnly,
} from "../../../Components/Common/FormatDate";

const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";
const MAX_VISIBLE_CAMPAIGNS = 3;

const AdLogsDetailTable = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const batchCodeParam = searchParams.get("batchCode");
  const [adLogsList, setAdLogsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    advertiserId: "",
    campaignId: "",
    screenName: "",
    fromDate: "",
    toDate: "",
    batchCode: batchCodeParam || "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Modal states
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedAdDetails, setSelectedAdDetails] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: { style: { fontSize: "0.9rem" } },
  };

  // Update filters when batchCode parameter changes
  useEffect(() => {
    if (batchCodeParam) {
      setFilters((prev) => ({
        ...prev,
        batchCode: batchCodeParam,
      }));
    }
  }, [batchCodeParam]);

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch ad logs
  async function fetchAdLogsList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);

      const response = await AdLogsService.getAdLogs({
        advertiserId: currentFilters.advertiserId || undefined,
        campaignId: currentFilters.campaignId || undefined,
        screenName: currentFilters.screenName || undefined,
        batchCode: currentFilters.batchCode || undefined,
        fromDate: currentFilters.fromDate
          ? new Date(currentFilters.fromDate).toISOString()
          : undefined,
        toDate: currentFilters.toDate
          ? new Date(currentFilters.toDate).toISOString()
          : undefined,
        pageNumber: page,
        pageSize: size,
      });

      console.log("🚀 ~ fetchAdLogsList ~ response:", response);

      if (response) {
        setAdLogsList(response.data || []);
        setTotalRows(response.totalRecords || 0);
      }
    } catch (error) {
      console.error("Error fetching ad logs:", error);
      toast.error("Failed to fetch ad logs");
    } finally {
      setLoading(false);
    }
  }

  // Fetch logs when page, size, or filters change
  useEffect(() => {
    fetchAdLogsList(currentPage, pageSize);
  }, [currentPage, pageSize, debouncedFilters]);

  // Fetch ad details for preview modal
  const handleRowClick = async (row) => {
    try {
      setIsPreviewOpen(true);
      setPreviewLoading(true);
      setShowAllCampaigns(false);
      setSelectedAdDetails(null);

      const response = await AdsService.getAdById(row.adId);
      console.log("🚀 ~ Ad details response:", response);

      if (response) {
        setSelectedAdDetails(response);
      }
    } catch (error) {
      console.error("Error fetching ad details:", error);
      toast.error("Failed to fetch ad details");
      setIsPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      advertiserId: "",
      campaignId: "",
      screenName: "",
      fromDate: "",
      toDate: "",
      batchCode: batchCodeParam || "", // Keep batch code if it came from URL param
    });
    setCurrentPage(1);
  };

  // Transform ad logs data for Excel export
  const transformDataForExport = (data) => {
    return data.map((row, index) => ({
      "SN": index + 1,
      "Ad Name": row.adName || "-",
      "Advertiser": row.advertiserName || "-",
      "Screen Name": row.screenName || "-",
      "Device Name": row.deviceName || "-",
      "Shown At": row.shownAt
        ? formatDateTimeWithSeconds(new Date(row.shownAt))
        : "-",
      "Visible Seconds": row.visibleSeconds || 0,
      "Played Seconds": row.playedSeconds || 0,
      "Completed": row.wasCompleted ? "Yes" : "No",
    }));
  };

  // Fetch function for export
  const fetchAdLogsForExport = async (params) => {
    const response = await AdLogsService.getAdLogs({
      advertiserId: params.advertiserId || undefined,
      campaignId: params.campaignId || undefined,
      screenName: params.screenName || undefined,
      batchCode: params.batchCode || undefined,
      fromDate: params.fromDate
        ? new Date(params.fromDate).toISOString()
        : undefined,
      toDate: params.toDate
        ? new Date(params.toDate).toISOString()
        : undefined,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    });
    return response;
  };

  const columns = [
    {
      name: "SN",
      cell: (row, index) => (
        <div>{(currentPage - 1) * pageSize + index + 1}</div>
      ),
      width: "60px",
      sortable: false,
    },
    {
      name: "Ad Name",
      selector: (row) => row.adName || "-",
      cell: (row) => (
        <div style={{ cursor: "pointer", color: "#435ebe", fontWeight: "500" }}>
          {row.adName || "-"}
        </div>
      ),
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Advertiser",
      selector: (row) => row.advertiserName || "-",
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Screen Name",
      selector: (row) => row.screenName || "-",
      sortable: true,
      width: "120px",
    },
    {
      name: "Device Name",
      selector: (row) => row.deviceName || "-",
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Shown At",
      cell: (row) => {
        if (!row.shownAt) return "-";
        return formatDateTimeWithSeconds(new Date(row.shownAt));
      },
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Visible Seconds",
      selector: (row) => row.visibleSeconds || 0,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div>
          <span className="badge bg-info">{row.visibleSeconds}s</span>
        </div>
      ),
    },
    {
      name: "Played Seconds",
      selector: (row) => row.playedSeconds || 0,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div>
          <span className="badge bg-success">{row.playedSeconds}s</span>
        </div>
      ),
    },
    {
      name: "Completed",
      cell: (row) => (
        <div>
          {row.wasCompleted ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-danger">No</span>
          )}
        </div>
      ),
      sortable: true,
      width: "110px",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Logs Detail" pageTitle="Ad Management" />

          {/* Filter Card */}
          <AdLogsDetailFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
          />

          {/* Data Table */}
          <Row className="">
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Ad Logs Details{" "}
                      <small className="text-muted">({totalRows} total)</small>
                      {filters.batchCode && (
                        <span className="ms-3">
                          <span className="badge bg-info">
                            Batch: {filters.batchCode}
                          </span>
                        </span>
                      )}
                    </h5>
                    <ExportToExcel
                      fetchDataFunction={fetchAdLogsForExport}
                      filters={debouncedFilters}
                      fileName="Ad_Logs_Details"
                      dataTransformer={transformDataForExport}
                      buttonText="Export"
                      buttonSize="sm"
                      buttonColor="success"
                    />
                  </div>
                </CardHeader>
                <CardBody>
                  {loading && !adLogsList.length ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Triangle color="#435ebe" height={80} width={80} />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={adLogsList}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={pageSize}
                        paginationRowsPerPageOptions={[10, 20, 50, 100]}
                        onChangePage={(page) => setCurrentPage(page)}
                        onChangeRowsPerPage={(newSize, page) => {
                          setPageSize(newSize);
                          setCurrentPage(1);
                        }}
                        onRowClicked={handleRowClick}
                        pointerOnHover
                        progressPending={loading}
                        customStyles={customStyles}
                        noDataComponent={
                          <div className="text-center py-5">
                            <p className="text-muted">
                              No ad logs found. Try adjusting your filters.
                            </p>
                          </div>
                        }
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Ad Details Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        toggle={() => setIsPreviewOpen(false)}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <div className="modal-header border-0 pb-0">
          <button
            type="button"
            className="btn-close"
            onClick={() => setIsPreviewOpen(false)}
            aria-label="Close"
          />
        </div>

        <div className="modal-body p-0">
          {previewLoading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: "400px" }}
            >
              <Triangle color="#435ebe" height={60} width={60} />
            </div>
          ) : selectedAdDetails ? (
            <Row className="g-0">
              {/* LEFT SIDE PREVIEW */}
              <Col md="7" className="bg-light">
                <div
                  className="p-4 d-flex align-items-center justify-content-center"
                  style={{ minHeight: "500px" }}
                >
                  {selectedAdDetails.mediaPath ? (
                    selectedAdDetails.mediaType === "Video" ? (
                      <video
                        src={`${CDN_BASE_URL}${selectedAdDetails.mediaPath}`}
                        controls
                        autoPlay
                        className="w-100 shadow-sm"
                        style={{
                          borderRadius: "4px",
                          maxHeight: "500px",
                        }}
                        onError={(e) => {
                          console.error("Video load error:", e);
                          toast.error("Failed to load video");
                        }}
                      />
                    ) : (
                      <img
                        src={`${CDN_BASE_URL}${selectedAdDetails.mediaPath}`}
                        alt={selectedAdDetails.title}
                        className="w-100 shadow-sm"
                        style={{
                          borderRadius: "4px",
                          objectFit: "contain",
                          maxHeight: "500px",
                        }}
                        onError={(e) => {
                          console.error("Image load error:", e);
                          toast.error("Failed to load image");
                        }}
                      />
                    )
                  ) : (
                    <div
                      className="text-center text-muted"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <div style={{ fontSize: "3rem", marginBottom: "10px" }}>
                        📄
                      </div>
                      <p>No media available</p>
                    </div>
                  )}
                </div>
              </Col>

              {/* RIGHT SIDE DETAILS */}
              <Col md="5">
                <div className="p-4">
                  <h3
                    className="mb-1 fw-semibold"
                    style={{ fontSize: "1.5rem", color: "#1a1a1a" }}
                  >
                    {selectedAdDetails.title}
                  </h3>

                  <p
                    className="text-secondary mb-4"
                    style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                  >
                    {selectedAdDetails.description ||
                      "No description available"}
                  </p>

                  <div className="border-top pt-4">
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Advertiser
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.advertiserName}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Format
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.adFormat}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Media Type
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.mediaType}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Priority
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.priority}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Status
                          </div>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: selectedAdDetails.isActive
                                ? "#e6f7ed"
                                : "#f5f5f5",
                              color: selectedAdDetails.isActive
                                ? "#0f5132"
                                : "#6c757d",
                              fontWeight: "500",
                              padding: "4px 10px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {selectedAdDetails.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Duration
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.durationSeconds
                              ? `${selectedAdDetails.durationSeconds}s`
                              : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 mt-3">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Campaigns (
                            {selectedAdDetails.campaigns?.length || 0})
                          </div>
                          {selectedAdDetails.campaigns &&
                          selectedAdDetails.campaigns.length > 0 ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                  marginBottom: "8px",
                                }}
                              >
                                {(showAllCampaigns
                                  ? selectedAdDetails.campaigns
                                  : selectedAdDetails.campaigns.slice(
                                      0,
                                      MAX_VISIBLE_CAMPAIGNS,
                                    )
                                ).map((camp) => (
                                  <span
                                    key={camp.id}
                                    className="badge"
                                    style={{
                                      backgroundColor: camp.isActive
                                        ? "#e6f7ed"
                                        : "#fdecea",
                                      color: camp.isActive
                                        ? "#0f5132"
                                        : "#842029",
                                      fontWeight: "500",
                                      padding: "6px 12px",
                                      fontSize: "0.85rem",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                    }}
                                    onClick={() =>
                                      navigate(`/campaign/view/${camp.id}`)
                                    }
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = "scale(1.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = "scale(1)";
                                    }}
                                  >
                                    {camp.name} · {camp.adsCount}
                                  </span>
                                ))}
                              </div>
                              {selectedAdDetails.campaigns.length >
                                MAX_VISIBLE_CAMPAIGNS && (
                                <button
                                  className="btn btn-link p-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    textDecoration: "none",
                                  }}
                                  onClick={() =>
                                    setShowAllCampaigns(!showAllCampaigns)
                                  }
                                >
                                  {showAllCampaigns
                                    ? "Show Less"
                                    : `+${
                                        selectedAdDetails.campaigns.length -
                                        MAX_VISIBLE_CAMPAIGNS
                                      } More`}
                                </button>
                              )}
                            </>
                          ) : (
                            <div
                              style={{ fontSize: "0.95rem", color: "#6c757d" }}
                            >
                              No campaigns
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Target
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.targetAllDevices
                              ? "All Devices"
                              : "Specific Devices"}
                          </div>
                        </div>
                      </div>
                      {selectedAdDetails.tags &&
                        selectedAdDetails.tags.length > 0 && (
                          <div className="col-12">
                            <div className="mb-3">
                              <div
                                className="text-uppercase text-muted mb-1"
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Tags
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                }}
                              >
                                {selectedAdDetails.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="badge"
                                    style={{
                                      backgroundColor: "#f0f2f5",
                                      color: "#495057",
                                      fontWeight: "400",
                                      padding: "4px 10px",
                                      fontSize: "0.85rem",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          ) : null}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default AdLogsDetailTable;
